import { CheckCircle2, ChevronRight, PencilLine, Plus } from "lucide-react-native";
import { useCallback, useEffect, useId, useRef, useState } from "react";
import { Keyboard, Platform, Pressable, StyleSheet, Text, useWindowDimensions, View } from "react-native";

import {
  attendanceErrorMessage,
  buildAttendanceReceiptLink,
  correctPublicAttendance,
  submitPublicAttendance,
  type PublicAttendanceInput,
} from "../lib/central-attendance";
import * as Clipboard from "../lib/clipboard";
import { missingGuestAddressFields, validateGuestRegisterAddress, validateGuestRegisterContact } from "../lib/guest-register-validation";
import { central, spacing } from "../theme/tokens";
import { Button, Field, Panel } from "./ui";
import { RegisterSignInButton } from "./RegisterSignInButton";
import { RegisterStreetAddress } from "./RegisterStreetAddress";

type Receipt = { partySize: number; receiptToken: string };
type PublicAttendanceApi = {
  correct: typeof correctPublicAttendance;
  submit: typeof submitPublicAttendance;
};

const emptyAddress = () => ({ street: "", unit: "", city: "", state: "", zip: "" });

const defaultApi: PublicAttendanceApi = {
  correct: correctPublicAttendance,
  submit: submitPublicAttendance,
};

export function PublicAttendanceCheckIn({
  api = defaultApi,
  brand = "default",
  eventId,
  eventLabel,
  funeralHomeId,
  initialReceiptToken = "",
  lovedOneName = "",
  funeralHomeName = "the funeral home",
  kiosk = false,
  inlineRegister = false,
  onReset,
  onComplete,
  onAddressEditingChange,
  slug,
  source = "direct",
}: {
  api?: PublicAttendanceApi;
  brand?: "default" | "monarch";
  eventId: string;
  eventLabel: string;
  funeralHomeId: string;
  initialReceiptToken?: string;
  lovedOneName?: string;
  funeralHomeName?: string;
  kiosk?: boolean;
  inlineRegister?: boolean;
  onReset?: () => void;
  onComplete?: () => void;
  onAddressEditingChange?: (editing: boolean) => void;
  slug: string;
  source?: "direct" | "manual" | "qr";
}) {
  const { height, width } = useWindowDimensions();
  const compactRegister = inlineRegister && height < 740;
  const [form, setForm] = useState({ consent: false, email: "", mailingAddress: "", householdName: "", name: "", partySize: "1", phone: "", relationship: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [receipt, setReceipt] = useState<Receipt | null>(!kiosk && initialReceiptToken ? { partySize: 0, receiptToken: initialReceiptToken } : null);
  const [correcting, setCorrecting] = useState(!kiosk && Boolean(initialReceiptToken));
  const [correction, setCorrection] = useState({ householdName: "", name: "", partySize: "", reason: "", relationship: "" });
  const [correctionComplete, setCorrectionComplete] = useState(false);
  const [copied, setCopied] = useState(false);
  const [idleWarning, setIdleWarning] = useState(false);
  const contactValidation = validateGuestRegisterContact(form);
  const [addressExpanded, setAddressExpanded] = useState(false);
  const [addressDraft, setAddressDraft] = useState(emptyAddress);
  const [savedAddress, setSavedAddress] = useState(emptyAddress);
  const [addressAttempted, setAddressAttempted] = useState(false);
  const [addressPending, setAddressPending] = useState(false);
  const addressTriggerId = useId();
  const addressValid = validateGuestRegisterAddress(savedAddress);
  const addressDraftValid = validateGuestRegisterAddress(addressDraft);
  const familyName = lovedOneName.trim().split(/\s+/)[0];
  const informationUseStatement = `The above information is for use by ${familyName ? `${familyName}’s family` : "the family"} and ${funeralHomeName}.`;
  const partySize = Number(form.partySize);
  const validPartySize = Number.isInteger(partySize) && partySize >= 1 && partySize <= 50;
  const kioskInputProps = kiosk ? { autoComplete: "off" as const, autoCorrect: false, spellCheck: false, textContentType: "none" as const } : {};

  const consentKeyboardProps = Platform.OS === "web" ? {
    onKeyDown: (event: { key: string; preventDefault: () => void }) => {
      // React Native Web handles Enter, but only handles Space for button roles.
      if (event.key === " " && !busy) {
        event.preventDefault();
        setForm(current => ({ ...current, consent: !current.consent }));
      }
    },
  } : {};

  const pendingSubmission = useRef<{ fingerprint: string; key: string } | null>(null);
  const inFlight = useRef(false);
  const resetGuest = useCallback(() => {
    setForm({ consent: false, email: "", mailingAddress: "", householdName: "", name: "", partySize: "1", phone: "", relationship: "" });
    setCorrection({ householdName: "", name: "", partySize: "", reason: "", relationship: "" });
    setReceipt(null);
    setCorrecting(false);
    setCorrectionComplete(false);
    setCopied(false);
    setIdleWarning(false);
    setError("");
    setAddressExpanded(false);
    setAddressAttempted(false);
    setAddressPending(false);
    setAddressDraft(emptyAddress());
    setSavedAddress(emptyAddress());
    onAddressEditingChange?.(false);
    pendingSubmission.current = null;
    onReset?.();
  }, [onReset, onAddressEditingChange]);

  useEffect(() => {
    if (!kiosk || !receipt) return;
    const timer = setTimeout(resetGuest, inlineRegister ? 5_000 : 10_000);
    return () => clearTimeout(timer);
  }, [kiosk, receipt, resetGuest, inlineRegister]);

  useEffect(() => {
    setIdleWarning(false);
    if (!kiosk || receipt || busy || ((inlineRegister || !onReset) && !addressExpanded && !Object.entries(form).some(([key, value]) => key === "partySize" ? value !== "1" : Boolean(value)))) return;
    const warning = setTimeout(() => setIdleWarning(true), 90_000);
    const clear = setTimeout(resetGuest, 120_000);
    return () => { clearTimeout(warning); clearTimeout(clear); };
  }, [kiosk, receipt, busy, form, resetGuest, onReset, inlineRegister, addressExpanded, addressDraft]);

  const openAddress = () => {
    setAddressAttempted(false);
    setAddressPending(false);
    setAddressDraft({ ...savedAddress });
    onAddressEditingChange?.(true);
    setAddressExpanded(true);
  };
  const closeAddress = (save: boolean) => {
    if (save) {
      if (addressPending) return;
      if (!addressDraftValid) { setAddressAttempted(true); return; }
      const address = Object.fromEntries(Object.entries(addressDraft).map(([key, value]) => [key, value.trim()])) as typeof addressDraft;
      const locality = [address.city, [address.state, address.zip].filter(Boolean).join(" ")].filter(Boolean).join(", ");
      setSavedAddress(address);
      setForm(current => ({ ...current, mailingAddress: [address.street, address.unit, locality].filter(Boolean).join("\n") }));
    }
    Keyboard.dismiss();
    setAddressExpanded(false);
    onAddressEditingChange?.(false);
    if (Platform.OS === "web" && typeof document !== "undefined") {
      requestAnimationFrame(() => document.getElementById(addressTriggerId)?.focus({ preventScroll: true }));
    }
  };

  const submit = async () => {
    if (inFlight.current || busy || !validPartySize || form.name.trim().length < 2 || (inlineRegister && (!contactValidation.valid || !addressValid))) return;
    inFlight.current = true;
    const fingerprint = JSON.stringify({ eventId, funeralHomeId, slug, form: { ...form, name: form.name.trim(), email: form.email.trim().toLowerCase(), phone: form.phone.trim(), householdName: form.householdName.trim(), relationship: form.relationship.trim(), partySize } });
    if (pendingSubmission.current?.fingerprint !== fingerprint) {
      pendingSubmission.current = { fingerprint, key: operationKey("attendance") };
    }
    setBusy(true);
    setError("");
    try {
      const input: PublicAttendanceInput = {
        email: form.email.trim(),
        mailingAddress: form.mailingAddress.trim(),
        eventId,
        followUpConsent: !inlineRegister && form.consent ? { channel: form.email.trim() ? "email" : "sms", granted: true, textVersion: "attendance-follow-up-v1" } : undefined,
        funeralHomeId,
        householdName: form.householdName.trim(),
        idempotencyKey: pendingSubmission.current.key,
        name: form.name.trim(),
        partySize,
        phone: form.phone.trim(),
        relationship: form.relationship.trim(),
        slug,
        source,
      };
      const result = await api.submit(input);
      onComplete?.();
      setReceipt(result);
      if (kiosk) {
        Keyboard.dismiss();
        setForm({ consent: false, email: "", mailingAddress: "", householdName: "", name: "", partySize: "1", phone: "", relationship: "" });
      } else {
        setCorrection({ householdName: form.householdName, name: form.name, partySize: form.partySize, reason: "", relationship: form.relationship });
      }
    } catch (caught) {
      setError(attendanceErrorMessage(caught));
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };

  const submitCorrection = async () => {
    if (!receipt || busy || correction.reason.trim().length < 3 || !hasCorrection(correction)) return;
    setBusy(true);
    setError("");
    try {
      await api.correct({
        receiptToken: receipt.receiptToken,
        reason: correction.reason,
        ...(correction.name.trim() ? { name: correction.name } : {}),
        ...(correction.householdName.trim() ? { householdName: correction.householdName } : {}),
        ...(correction.partySize ? { partySize: Number(correction.partySize) } : {}),
        ...(correction.relationship.trim() ? { relationship: correction.relationship } : {}),
      });
      setCorrectionComplete(true);
      setCorrecting(false);
    } catch (caught) {
      setError(attendanceErrorMessage(caught));
    } finally {
      setBusy(false);
    }
  };

  if (correcting && receipt) {
    const canCorrect = correction.reason.trim().length >= 3 && hasCorrection(correction);
    return (
      <Panel style={[styles.panel, brand === "monarch" ? styles.monarchPanel : null]}>
        <View style={styles.headingRow}>
          <PencilLine color={central.action.primary} size={24} />
          <View style={styles.copy}>
            <Text accessibilityRole="header" style={styles.title}>Correct your check-in</Text>
            <Text style={styles.muted}>Enter only the details that should change. This private receipt never displays the attendance record or another guest’s information.</Text>
          </View>
        </View>
        <View style={styles.fields}>
          <Field {...kioskInputProps} brand={brand} containerStyle={styles.field} label="Name" value={correction.name} onChangeText={(name) => setCorrection((current) => ({ ...current, name }))} placeholder="Leave blank to keep unchanged" />
          <Field {...kioskInputProps} brand={brand} containerStyle={styles.field} label="People Arriving Together" value={correction.partySize} onChangeText={(partySize) => setCorrection((current) => ({ ...current, partySize }))} placeholder="Leave blank to keep unchanged" keyboardType="number-pad" />
          <Field {...kioskInputProps} brand={brand} containerStyle={styles.field} label="Household or Group Name" value={correction.householdName} onChangeText={(householdName) => setCorrection((current) => ({ ...current, householdName }))} placeholder="Leave blank to keep unchanged" />
          <Field {...kioskInputProps} brand={brand} containerStyle={styles.field} label="Relationship" value={correction.relationship} onChangeText={(relationship) => setCorrection((current) => ({ ...current, relationship }))} placeholder="Leave blank to keep unchanged" />
          <Field brand={brand} containerStyle={styles.fullField} error={correction.reason.length > 0 && correction.reason.trim().length < 3 ? "Briefly explain the correction." : undefined} label="Reason for correction" required multiline value={correction.reason} onChangeText={(reason) => setCorrection((current) => ({ ...current, reason }))} placeholder="For example: party size entered incorrectly" />
        </View>
        {error ? <Text accessibilityLiveRegion="assertive" style={styles.error}>{error}</Text> : null}
        <View style={styles.actions}>
          <Button brand={brand} label="Submit correction" disabled={!canCorrect || busy} disabledHint="Enter a changed detail and a brief reason." loading={busy} onPress={() => void submitCorrection()} shape="rounded" />
          {!initialReceiptToken ? <Button brand={brand} label="Cancel" onPress={() => { setCorrecting(false); setError(""); }} shape="rounded" variant="secondary" /> : null}
        </View>
      </Panel>
    );
  }

  if (receipt && kiosk) {
    return <Panel style={[styles.success, inlineRegister && styles.inlineSuccess, compactRegister && styles.inlineCompact]}>
      <CheckCircle2 color={central.action.primary} size={30} />
      <View style={styles.copy}>
        <Text accessibilityRole="header" accessibilityLiveRegion="polite" style={styles.successTitle}>Thank You for Being Here</Text>
        <Text style={styles.muted}>Your attendance has been saved. The form will be ready for the next guest in {inlineRegister ? "5" : "10"} seconds.</Text>
        <Button label="Next Guest" onPress={resetGuest} shape="rounded" />
      </View>
    </Panel>;
  }

  if (receipt) {
    return (
      <Panel style={[styles.success, brand === "monarch" ? styles.monarchPanel : null]}>
        <CheckCircle2 color="#10265E" size={30} />
        <View style={styles.copy}>
          <Text accessibilityRole="header" style={styles.successTitle}>{correctionComplete ? "Your correction was received." : "You are checked in."}</Text>
          <Text style={styles.muted}>
            {correctionComplete
              ? "The funeral home can review the correction while the original check-in remains in its private history."
              : `${eventLabel} · ${receipt.partySize} ${receipt.partySize === 1 ? "person" : "people"}.`}
          </Text>
          <Text style={styles.muted}>Private receipt link ending in {receipt.receiptToken.slice(-8)}. It contains no contact details, but anyone holding it can request a correction.</Text>
          <View style={styles.actions}>
            <Button brand={brand} compact label={copied ? "Private receipt link copied" : "Copy private receipt link"} onPress={() => void Clipboard.setStringAsync(buildAttendanceReceiptLink(receipt.receiptToken)).then(() => setCopied(true))} shape="rounded" variant="secondary" />
            <Button brand={brand} compact label="Correct check-in details" onPress={() => { setCorrecting(true); setCorrectionComplete(false); setError(""); }} shape="rounded" variant="secondary" />
          </View>
        </View>
      </Panel>
    );
  }

  if (inlineRegister && addressExpanded) {
    return <Panel style={[styles.panel, styles.inlinePanel, compactRegister && styles.inlineCompact, compactRegister && width < 360 && styles.addressCompact]}>
      <Text accessibilityRole="header" style={styles.title}>Mailing Address</Text>
      {addressPending || (addressAttempted && !addressDraftValid) ? <Text accessibilityLiveRegion={addressPending ? "polite" : "assertive"} style={[styles.muted, !addressPending && styles.addressValidation]}>{addressPending ? "Filling your address…" : `Please complete: ${missingGuestAddressFields(addressDraft).join(", ")}.`}</Text> : null}
      <View style={styles.addressFields}>
        <RegisterStreetAddress onPendingChange={setAddressPending} context={{ funeralHomeId, slug, eventId }} value={addressDraft.street} onChange={street => setAddressDraft(current => ({ ...current, street }))} onSelect={address => setAddressDraft(current => ({ ...address, unit: address.unit || current.unit }))} />
        <Field {...kioskInputProps} label="Apt, Suite, Etc. (Optional)" value={addressDraft.unit} onChangeText={unit => setAddressDraft(current => ({ ...current, unit }))} placeholder="Apartment or suite" maxLength={80} inputStyle={styles.compactInput} />
        <View style={styles.addressLocality}>
          <Field {...kioskInputProps} containerStyle={styles.addressCity} label="City" required value={addressDraft.city} onChangeText={city => setAddressDraft(current => ({ ...current, city }))} placeholder="City" autoCapitalize="words" maxLength={100} inputStyle={styles.compactInput} />
          <Field {...kioskInputProps} containerStyle={styles.addressState} label="State" required value={addressDraft.state} onChangeText={state => setAddressDraft(current => ({ ...current, state }))} placeholder="FL" autoCapitalize="characters" maxLength={50} inputStyle={styles.compactInput} />
          <Field {...kioskInputProps} containerStyle={styles.addressZip} label="ZIP Code" required value={addressDraft.zip} onChangeText={zip => setAddressDraft(current => ({ ...current, zip }))} placeholder="ZIP" keyboardType="numbers-and-punctuation" maxLength={20} inputStyle={styles.compactInput} />
        </View>
      </View>
      <View style={styles.addressActions}>
        <View style={styles.addressAction}><Button fullWidth label="Back" accessibilityHint="Discard address changes and return to sign in." variant="secondary" onPress={() => closeAddress(false)} shape="rounded" /></View>
        <View style={styles.addressAction}><Button fullWidth label={addressPending ? "Filling Address…" : "Done"} disabled={addressPending} onPress={() => closeAddress(true)} shape="rounded" /></View>
      </View>
    </Panel>;
  }

  return (
    <Panel style={[styles.panel, brand === "monarch" ? styles.monarchPanel : null, inlineRegister && styles.inlinePanel, compactRegister && styles.inlineCompact, inlineRegister && compactRegister && width < 360 && styles.addressCompact]}>
      <Text accessibilityRole="header" style={[styles.title, inlineRegister && styles.registerTitle, inlineRegister && compactRegister && styles.registerTitleCompact, inlineRegister && width < 360 && styles.registerTitleSmall]}>{inlineRegister ? "Your Presence Is A Comfort" : kiosk ? "Sign the Register" : `Check in for ${eventLabel}`}</Text>
      {!inlineRegister ? <Text style={styles.muted}>No camera, account, phone number, or email is required. Share only what you want the funeral home to have.</Text> : null}
      <View style={[styles.fields, inlineRegister && styles.registerFields, compactRegister && styles.registerFieldsCompact]}>
        <Field {...kioskInputProps} brand={brand} inputStyle={inlineRegister ? styles.privateNameInput : undefined} containerStyle={inlineRegister ? styles.fullField : styles.field} multiline={inlineRegister} blurOnSubmit={inlineRegister ? true : undefined} returnKeyType="next" label={inlineRegister ? "Full Name" : "Your Name"} required value={form.name} onChangeText={(name) => setForm((current) => ({ ...current, name: inlineRegister ? name.replace(/[\r\n]+/g, " ") : name }))} placeholder={inlineRegister ? "Your entry is private. Other guests cannot see your details." : "Your full name"} accessibilityHint={inlineRegister ? "Your entry is private. Other guests cannot see your details." : undefined} autoCapitalize="words" />
        {inlineRegister ? <>
          <Field {...kioskInputProps} brand={brand} inputStyle={compactRegister ? styles.compactInput : undefined} containerStyle={styles.contactField} label="Phone" required value={form.phone} onChangeText={(phone) => setForm(current => ({ ...current, phone }))} placeholder="Phone number" keyboardType="phone-pad" error={form.phone.trim() ? contactValidation.phoneError || undefined : undefined} />
          <Field {...kioskInputProps} brand={brand} inputStyle={compactRegister ? styles.compactInput : undefined} containerStyle={styles.contactField} label="Email (Optional)" value={form.email} onChangeText={(email) => setForm(current => ({ ...current, email }))} placeholder="Email address" keyboardType="email-address" autoCapitalize="none" error={form.email.trim() ? contactValidation.emailError || undefined : undefined} />
        </> : null}
        {!inlineRegister ? <>
        <Field {...kioskInputProps} brand={brand} containerStyle={styles.field} label="People Arriving Together" value={form.partySize} onChangeText={(partySize) => setForm((current) => ({ ...current, partySize }))} keyboardType="number-pad" />
        <Field {...kioskInputProps} brand={brand} containerStyle={styles.field} label="Household or Group Name" value={form.householdName} onChangeText={(householdName) => setForm((current) => ({ ...current, householdName }))} placeholder="Optional" />
        <Field {...kioskInputProps} brand={brand} containerStyle={styles.field} label="Relationship" value={form.relationship} onChangeText={(relationship) => setForm((current) => ({ ...current, relationship }))} placeholder="Optional" />
        <Field {...kioskInputProps} brand={brand} containerStyle={styles.field} label="Email" value={form.email} onChangeText={(email) => setForm((current) => ({ ...current, email }))} placeholder="Optional" keyboardType="email-address" autoCapitalize="none" />
        <Field {...kioskInputProps} brand={brand} containerStyle={styles.field} label="Phone" value={form.phone} onChangeText={(phone) => setForm((current) => ({ ...current, phone }))} placeholder="Optional" keyboardType="phone-pad" />
        <Field {...kioskInputProps} brand={brand} containerStyle={styles.fullField} label="Mailing Address (Optional)" multiline value={form.mailingAddress} onChangeText={mailingAddress => setForm(current => ({ ...current, mailingAddress }))} placeholder="Street address, city, state/province, postal code" hint="Optional. For the family to send a thank-you note." maxLength={500} />
        </> : null}
      </View>


      {!inlineRegister && form.consent && !form.email.trim() && !form.phone.trim() ? <Text accessibilityLiveRegion="polite" style={styles.help}>Add an email or phone only if you want follow-up.</Text> : null}
      {inlineRegister ? <View style={styles.addressRow}>
        <Text style={styles.addressLabel}>Mailing Address <Text style={styles.requiredMark}>*</Text></Text>
        <Pressable nativeID={addressTriggerId} accessibilityRole="button" accessibilityLabel={form.mailingAddress.trim() ? "Edit Mailing Address" : "Enter Mailing Address"} accessibilityHint="Open the mailing address form." accessibilityState={{ disabled: busy, expanded: addressExpanded }} aria-expanded={addressExpanded} disabled={busy} onPress={openAddress} style={({ pressed }) => [styles.addressToggle, (pressed || busy) && styles.addressTogglePressed]}>
          {form.mailingAddress.trim() ? <CheckCircle2 size={18} color={central.action.primary} /> : <Plus size={18} color={central.action.primary} />}
          <Text style={styles.addressActionLabel}>{form.mailingAddress.trim() ? "Edit Mailing Address" : "Enter Mailing Address"}</Text>
          <ChevronRight size={18} color={central.action.primary} />
        </Pressable>
      </View> : null}
      {inlineRegister ? <Text style={styles.informationNotice}>{informationUseStatement}</Text> : null}
      {!inlineRegister ? <Pressable {...consentKeyboardProps} accessibilityRole="checkbox" accessibilityLabel="Allow Optional Follow-Up" accessibilityHint="Optional. You can sign in without agreeing." accessibilityState={{ checked: form.consent, disabled: busy }} aria-checked={form.consent} disabled={busy} onPress={() => setForm(current => ({ ...current, consent: !current.consent }))} style={styles.consent}>
        <Text accessible={false} style={styles.checkbox}>{form.consent ? "✓" : ""}</Text>
        <Text style={styles.consentCopy}>{"I agree that the funeral home may use the contact method above for follow-up. This is optional and does not affect check-in."}</Text>
      </Pressable> : null}
      {error ? <Text accessibilityLiveRegion="assertive" style={styles.error}>{error}</Text> : null}
      {!validPartySize ? <Text accessibilityLiveRegion="polite" style={styles.error}>Enter a whole number from 1 to 50 for people arriving together.</Text> : null}
      {kiosk && (!inlineRegister || idleWarning) ? <Text accessibilityLiveRegion="polite" style={styles.help}>{idleWarning ? "Your unfinished details will clear shortly for privacy. Continue editing to keep them." : "For privacy, unfinished details clear after two minutes without changes."}</Text> : null}
      {inlineRegister ? <RegisterSignInButton disabled={!validPartySize || !contactValidation.valid || !addressValid} loading={busy} onPress={() => void submit()} /> : <Button brand={brand} label={kiosk ? "Sign the Register" : "Complete Check-In"} disabled={busy || !validPartySize || form.name.trim().length < 2 || (form.consent && !form.email.trim() && !form.phone.trim())} loading={busy} onPress={() => void submit()} shape="rounded" />}
      {kiosk && !inlineRegister ? <Button label={onReset ? "Return to Welcome" : "Clear My Details"} disabled={busy} onPress={resetGuest} shape="rounded" variant="ghost" /> : null}
    </Panel>
  );
}

function hasCorrection(value: { householdName: string; name: string; partySize: string; relationship: string }) {
  return Boolean(value.householdName.trim() || value.name.trim() || value.partySize || value.relationship.trim());
}

function operationKey(prefix: string) {
  const random = typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID().replaceAll("-", "") : Math.random().toString(36).slice(2);
  return `${prefix}_${Date.now()}_${random}`.slice(0, 150);
}

const styles = StyleSheet.create({
  actions: { flexDirection: "row", flexWrap: "wrap", gap: spacing.sm },
  checkbox: { borderColor: central.border.strong, borderRadius: 4, borderWidth: 1, color: central.text.primary, fontFamily: central.typography.label, fontSize: 15, height: 24, lineHeight: 22, textAlign: "center", width: 24 },
  consent: { alignItems: "flex-start", flexDirection: "row", gap: spacing.sm, minHeight: 44 },
  consentCopy: { color: central.text.secondary, flex: 1, fontFamily: central.typography.body, fontSize: 13, lineHeight: 20 },
  copy: { flex: 1, gap: spacing.xs, minWidth: 0 },
  error: { backgroundColor: central.status.error.background, color: central.status.error.foreground, fontFamily: central.typography.label, padding: spacing.sm },
  field: { flexBasis: 240, flexGrow: 1, minWidth: 0 },
  fields: { flexDirection: "row", flexWrap: "wrap", gap: spacing.sm },
  fullField: { flexBasis: "100%", flexGrow: 1, minWidth: 0 },
  headingRow: { alignItems: "flex-start", flexDirection: "row", gap: spacing.sm },
  help: { color: central.status.info.foreground, fontFamily: central.typography.bodyMedium, fontSize: 13 },
  muted: { color: central.text.secondary, fontFamily: central.typography.body, fontSize: 13, lineHeight: 20, maxWidth: 720 },
  monarchPanel: { backgroundColor: "#FFFFFF", borderColor: "#D6DDE8", borderRadius: 14 },
  panel: { gap: spacing.md },
  inlineSuccess: { minHeight: 320, padding: 0, justifyContent: "center", borderWidth: 0, backgroundColor: "transparent", shadowOpacity: 0, elevation: 0, boxShadow: "none" },
  inlineCompact: { minHeight: 0, gap: 12 },
  informationNotice: { color: central.text.secondary, fontFamily: central.typography.body, fontSize: 13, lineHeight: 20 },
  privateNameInput: { minHeight: 64, height: 64, paddingVertical: 8, textAlignVertical: "center" },
  compactInput: { minHeight: 44, height: 44, paddingVertical: 8 },
  inlinePanel: { minHeight: 320, gap: 20, padding: 0, backgroundColor: "transparent", borderWidth: 0, shadowOpacity: 0, elevation: 0, boxShadow: "none" },
  addressValidation: { color: central.status.error.foreground },
  addressCompact: { gap: 6 },
  addressActions: { flexDirection: "row", gap: 12 },
  addressAction: { flex: 1, minWidth: 0 },
  addressFields: { gap: 8 },
  addressLocality: { flexDirection: "row", gap: 8 },
  addressCity: { flex: 2, minWidth: 0 },
  addressState: { flex: 1, minWidth: 0 },
  addressZip: { flex: 1.3, minWidth: 0 },
  addressRow: { gap: 8 },
  addressLabel: { color: central.text.primary, fontFamily: central.typography.bodyMedium, fontSize: 14, lineHeight: 20 },
  requiredMark: { color: central.status.error.foreground },
  addressActionLabel: { flex: 1, color: central.action.primary, fontFamily: central.typography.bodyMedium, fontSize: 14, lineHeight: 20 },
  addressToggle: { minHeight: 50, flexDirection: "row", justifyContent: "center", alignItems: "center", gap: 8, paddingHorizontal: 14, paddingVertical: 10, borderWidth: 1, borderColor: central.border.strong, borderRadius: 12, backgroundColor: central.surface.raised },
  addressTogglePressed: { opacity: 0.65 },
  addressToggleText: { color: central.action.primary, fontFamily: central.typography.bodyMedium, fontSize: 13, lineHeight: 18 },
  registerTitle: { textAlign: "center", fontFamily: "Newsreader_600SemiBold", fontSize: 32, lineHeight: 38, marginBottom: 4 },
  registerTitleCompact: { fontSize: 26, lineHeight: 30, marginBottom: 0 },
  registerTitleSmall: { fontSize: 22, lineHeight: 26 },
  registerFields: { gap: 16 },
  registerFieldsCompact: { gap: 12 },
  contactField: { flexBasis: 120, flexGrow: 1, minWidth: 0 },
  centeredNote: { textAlign: "center" },
  privateNote: { color: central.text.muted, fontFamily: central.typography.body, fontSize: 12, lineHeight: 18 },
  success: { alignItems: "flex-start", flexDirection: "row", flexWrap: "wrap", gap: spacing.md },
  successTitle: { color: "#10265E", fontFamily: central.typography.label, fontSize: 18 },
  title: { color: central.text.primary, fontFamily: central.typography.display, fontSize: 24, lineHeight: 30 },
});
